import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  List,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message
} from 'antd';
import { ReloadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { RecurrenceRule } from '../../../../shared/types/work-schedule.types';
import { RecurrenceEditor } from '../../components/WorkSchedule/RecurrenceEditor';
import { trpc } from '../../lib/api';
import { withDevModalFilePath } from '../../components/common';
import { getMutationPendingState } from '../../utils/trpcMutation';

const { Modal } = withDevModalFilePath('client/src/pages/Maintenance/RecurringSettings.tsx');
import type { WorkScheduleWithRelations } from '../../types/work-schedule';

const { Title, Text } = Typography;

const PATTERN_LABELS: Record<string, string> = {
  daily: '日次',
  weekly: '週次',
  monthly: '月次',
  yearly: '年次',
  custom: 'カスタム'
};

function getScheduleLabel(schedule: WorkScheduleWithRelations): string {
  return schedule.title?.trim() || schedule.displayId;
}

export default function RecurringSettingsPage(): JSX.Element {
  const [selectedPattern, setSelectedPattern] = useState<string | undefined>();
  const { data, isLoading, refetch } = trpc.schedule.getRecurringSchedules.useQuery();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<WorkScheduleWithRelations | null>(null);
  const [editingRule, setEditingRule] = useState<RecurrenceRule | null>(null);
  const [messageApi, messageContextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();

  const updateRecurrence = trpc.schedule.updateRecurrence.useMutation();
  const deleteScheduleMutation = trpc.schedule.delete.useMutation();

  const schedules = useMemo<WorkScheduleWithRelations[]>(() => {
    if (!data) {
      return [];
    }
    return Array.isArray(data) ? data : [];
  }, [data]);

  const filtered = useMemo(() => {
    if (!selectedPattern) {
      return schedules;
    }
    return schedules.filter((item) => item.recurrenceRule?.pattern === selectedPattern);
  }, [selectedPattern, schedules]);

  const patterns = useMemo(() => {
    const set = new Set<string>();
    schedules.forEach((schedule) => {
      if (schedule.recurrenceRule?.pattern) {
        set.add(schedule.recurrenceRule.pattern);
      }
    });
    return Array.from(set);
  }, [schedules]);

  const closeModal = useCallback(() => {
    setEditModalOpen(false);
    setEditingSchedule(null);
    setEditingRule(null);
  }, []);

  const openEditModal = useCallback((schedule: WorkScheduleWithRelations) => {
    setEditingSchedule(schedule);
    setEditingRule(schedule.recurrenceRule ?? null);
    setEditModalOpen(true);
  }, []);

  const mutateRecurrence = useCallback(
    async (schedule: WorkScheduleWithRelations, rule: RecurrenceRule | null, successMessage: string) => {
      try {
        await updateRecurrence.mutateAsync({ id: schedule.id, recurrenceRule: rule });
        messageApi.success(successMessage);
        await refetch();
      } catch (error) {
        const description =
          error instanceof Error ? error.message : '繰り返し設定の更新に失敗しました';
        messageApi.error(description);
        throw error;
      }
    },
    [updateRecurrence, refetch, messageApi]
  );

  const handleToggle = useCallback(
    (schedule: WorkScheduleWithRelations, enabled: boolean) => {
      if (!schedule.recurrenceRule && enabled) {
        openEditModal(schedule);
        return;
      }
      if (!schedule.recurrenceRule) {
        messageApi.info('繰り返し設定が見つかりません。');
        return;
      }

      const nextRule: RecurrenceRule = {
        ...schedule.recurrenceRule,
        enabled
      };

      if (!enabled) {
        modal.confirm({
          title: '繰り返し設定を無効化しますか？',
          content: `${getScheduleLabel(schedule)} の繰り返し生成を停止します。`,
          okText: '無効化',
          cancelText: 'キャンセル',
          onOk: () => mutateRecurrence(schedule, nextRule, '繰り返し設定を無効化しました')
        });
        return;
      }

      mutateRecurrence(schedule, nextRule, '繰り返し設定を有効化しました').catch(() => undefined);
    },
    [mutateRecurrence, openEditModal, messageApi, modal]
  );

  const handleDelete = useCallback(
    (schedule: WorkScheduleWithRelations) => {
      modal.confirm({
        title: '繰り返しスケジュールを削除しますか？',
        content: `${getScheduleLabel(schedule)} の繰り返し設定と生成予定を削除します。`,
        okText: '削除',
        okButtonProps: { danger: true },
        cancelText: 'キャンセル',
        onOk: async () => {
          try {
            await deleteScheduleMutation.mutateAsync({ id: schedule.id });
            messageApi.success('繰り返し設定を削除しました');
            await refetch();
          } catch (error) {
            const description =
              error instanceof Error ? error.message : '繰り返し設定の削除に失敗しました';
            messageApi.error(description);
            throw error;
          }
        }
      });
    },
    [deleteScheduleMutation, refetch, messageApi, modal]
  );

  const handleModalOk = useCallback(async () => {
    if (!editingSchedule) {
      return;
    }
    try {
      await mutateRecurrence(
        editingSchedule,
        editingRule,
        editingRule?.enabled
          ? '繰り返し設定を更新しました'
          : '繰り返し設定を無効化しました'
      );
      closeModal();
    } catch {
      // mutateRecurrence handles messaging
    }
  }, [editingSchedule, editingRule, mutateRecurrence, closeModal]);

  return (
    <>
      {messageContextHolder}
      {modalContextHolder}
      <Space direction="vertical" style={{ width: '100%' }} size={24}>
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>
            繰り返し設定一覧
          </Title>
          <Text type="secondary">繰り返し元スケジュールの概要と状態を確認できます。</Text>
        </Col>
        <Col>
          <Space>
            <Select
              allowClear
              placeholder="パターンで絞り込み"
              style={{ width: 200 }}
              options={patterns.map((pattern) => ({
                label: PATTERN_LABELS[pattern] ?? pattern,
                value: pattern
              }))}
              value={selectedPattern}
              onChange={(value) => setSelectedPattern(value ?? undefined)}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void refetch()}
              loading={isLoading}
            >
              再読み込み
            </Button>
          </Space>
        </Col>
      </Row>

      <Modal
        title="繰り返し設定の編集"
        open={editModalOpen}
        onCancel={closeModal}
        onOk={handleModalOk}
        okText="保存"
        confirmLoading={getMutationPendingState(updateRecurrence)}
        destroyOnHidden
      >
        <RecurrenceEditor
          value={editingRule}
          onChange={setEditingRule}
          startDate={
            editingSchedule?.scheduledDate
              ? new Date(editingSchedule.scheduledDate)
              : undefined
          }
        />
      </Modal>

      <List
        loading={isLoading}
        dataSource={filtered}
        locale={{ emptyText: '繰り返し設定が見つかりません' }}
        rowKey={(item) => item.id}
        renderItem={(item) => {
          const enabled = Boolean(item.recurrenceRule?.enabled);
          const pattern = item.recurrenceRule?.pattern ?? '';
          const interval = item.recurrenceRule?.interval ?? 1;
          const endDate = item.recurrenceRule?.endDate
            ? dayjs(item.recurrenceRule.endDate).format('YYYY/MM/DD')
            : '未設定';

          return (
            <List.Item
              key={item.id}
              actions={[
                <Switch
                  key="toggle"
                  checked={enabled}
                  onChange={(checked) => handleToggle(item, checked)}
                  loading={getMutationPendingState(updateRecurrence)}
                  disabled={getMutationPendingState(updateRecurrence) || getMutationPendingState(deleteScheduleMutation)}
                />,
                <Button
                  key="edit"
                  icon={<EditOutlined />}
                  type="link"
                  onClick={() => openEditModal(item)}
                  disabled={getMutationPendingState(updateRecurrence) || getMutationPendingState(deleteScheduleMutation)}
                >
                  編集
                </Button>,
                <Button
                  key="delete"
                  icon={<DeleteOutlined />}
                  type="link"
                  danger
                  onClick={() => handleDelete(item)}
                  loading={getMutationPendingState(deleteScheduleMutation)}
                  disabled={getMutationPendingState(updateRecurrence)}
                >
                  削除
                </Button>
              ]}
            >
              <Card style={{ width: '100%' }}>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Space wrap>
                    <Text strong>{getScheduleLabel(item)}</Text>
                    <Tag color={enabled ? 'green' : 'default'}>
                      {enabled ? '有効' : '無効'}
                    </Tag>
                    <Tag color="geekblue">
                      パターン: {PATTERN_LABELS[pattern] ?? pattern.toUpperCase()}
                    </Tag>
                    <Tag color="purple">間隔: {interval}</Tag>
                    {item.recurrenceRule?.includeChildren && (
                      <Tag color="magenta">子スケジュール含む</Tag>
                    )}
                  </Space>
                  <Text type="secondary">
                    開始: {dayjs(item.scheduledDate).format('YYYY/MM/DD HH:mm')}
                  </Text>
                  <Text type="secondary">終了: {endDate}</Text>
                  {item.description && <Text>{item.description}</Text>}
                </Space>
              </Card>
            </List.Item>
          );
        }}
      />
    </Space>
    </>
  );
}
